import type { EmailAttachmentMeta } from "./interfaces";

export type EmailAttachmentEntry = {
  id: string;
  meta: EmailAttachmentMeta;
};

export function isReferenceAttachment(meta: EmailAttachmentMeta): boolean {
  if (meta.sourceUrl?.trim()) return true;
  const odataType = meta.odataType ?? "";
  return odataType.includes("referenceAttachment");
}

export function isPreviewableAttachment(meta: EmailAttachmentMeta): boolean {
  const mime = meta.mimeType?.toLowerCase() ?? "";
  if (mime.startsWith("image/")) return true;
  if (mime === "application/pdf") return true;
  const filename = (meta.filename ?? "").toLowerCase();
  return filename.endsWith(".pdf");
}

export function toTypedPreviewFile(
  blob: Blob,
  mimeType: string,
  filename: string,
): File {
  return new File([blob], filename || "attachment", { type: mimeType });
}

/** Blob URL for iframe/img preview; MIME must match content or Chrome downloads instead of rendering. */
export function createPreviewObjectUrl(
  blob: Blob,
  mimeType: string,
  filename: string,
): string {
  if (blob.type === mimeType) {
    return URL.createObjectURL(blob);
  }
  return URL.createObjectURL(toTypedPreviewFile(blob, mimeType, filename));
}

export function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () =>
      reject(reader.error ?? new Error("Failed to read attachment blob"));
    reader.readAsDataURL(blob);
  });
}

export function resolveAttachmentMimeType(
  contentType: string | undefined,
  filename: string,
  fallbackContentType?: string,
): string {
  const normalized = contentType?.split(";")[0]?.trim().toLowerCase();
  if (normalized && normalized !== "application/octet-stream") {
    return normalized;
  }

  const fallback = fallbackContentType?.split(";")[0]?.trim().toLowerCase();
  if (fallback && fallback !== "application/octet-stream") {
    return fallback;
  }

  const lower = (filename ?? "").toLowerCase();
  if (lower.endsWith(".pdf")) return "application/pdf";
  if (lower.endsWith(".png")) return "image/png";
  if (lower.endsWith(".jpg") || lower.endsWith(".jpeg")) return "image/jpeg";
  if (lower.endsWith(".gif")) return "image/gif";
  if (lower.endsWith(".webp")) return "image/webp";
  if (lower.endsWith(".svg")) return "image/svg+xml";

  return normalized || "application/octet-stream";
}

export function getNonInlineAttachments(
  attachments: Record<string, EmailAttachmentMeta> | null | undefined,
): EmailAttachmentEntry[] {
  if (!attachments) return [];

  return Object.entries(attachments)
    .filter(([, meta]) => !meta.isInline)
    .map(([id, meta]) => ({ id, meta }))
    .sort((a, b) =>
      (a.meta.filename ?? "").localeCompare(b.meta.filename ?? ""),
    );
}

export function attachmentNeedsMetadataBackfill(
  attachments: Record<string, EmailAttachmentMeta> | null | undefined,
): boolean {
  return attachments == null;
}

export function getEmailAttachmentErrorMessage(
  status: number,
  detailMessage?: string | null,
): { message: string; reconnectMailbox?: boolean } {
  if (status === 404) {
    return { message: "Attachment no longer available." };
  }
  if (status === 413) {
    return {
      message:
        "This file is too large to preview here. Open the original message in your mail client.",
    };
  }
  if (status === 401 || status === 403) {
    return {
      message:
        detailMessage?.trim() ||
        "Your mailbox connection expired. Reconnect your email account in settings.",
      reconnectMailbox: true,
    };
  }
  if (detailMessage?.trim()) {
    return { message: detailMessage };
  }
  return { message: "Could not load this attachment." };
}
